import React, { useEffect, useRef, useState } from "react";
import "./ProfileImageComponent.css";
import { useDispatch } from "react-redux";
import { fetchProfileData } from "../../../CentralStore/ExportLMSTable/dataSlice";
import { getUserProfileImage, uploadProfilePic } from "../../../Services/EndPoints";
import { postDataWithFiles } from "../../../Services/Services";
import lmsToast from "../../DMS/Common/CustomToastifier/CustomToastifier";
import { useTranslation } from "react-i18next";
import DmsButton from "../../DMS/Common/DmsButton/DmsButton";
import avatar from "../../../Assets/Images/avatar.png";

const VALID_FILE_TYPES = ["image/png", "image/jpg", "image/jpeg"];
const MAX_PHOTO_SIZE = 5 * 1024 * 1024; // 5MB

export default function ProfileImageComponent({ profileData }) {
  const dispatch = useDispatch();
  const [imgSrc, setImgSrc] = useState(avatar); // Set default avatar initially
  const [errorMsg, setErrorMsg] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (profileData?.attachments_path) {
      setImgSrc(profileData.attachments_path); // photo uploaded through /user/uploadProfilePic
    } else if (profileData?.photo) {
      setImgSrc(
        `${process.env.REACT_APP_CORE_CONTEXT_PATH_NAME}${getUserProfileImage}/${profileData.id}`
      );
    } else {
      setImgSrc(avatar); // Fallback to the default avatar if no user image
    }
  }, [profileData]);

  const handleChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // allow picking the same file again
    if (!file) return;

    if (!VALID_FILE_TYPES.includes(file.type)) {
      setErrorMsg("Invalid file type. Please select a PNG or JPG image.");
      return;
    }
    if (file.size > MAX_PHOTO_SIZE) {
      setErrorMsg("Image exceeds 5MB. Please choose a smaller image.");
      return;
    }
    setErrorMsg("");
    setUploading(true);
    // POST /user/uploadProfilePic — form-data with the image under "file"
    // (the logged-in user comes from the token, so no id is sent)
    const formData = new FormData();
    formData.append("file", file);
    const res = await postDataWithFiles(uploadProfilePic, formData);
    setUploading(false);

    // postDataWithFiles returns [] when the request fails
    if (res && !Array.isArray(res) && String(res.status || "").toLowerCase() === "success") {
      if (res.attachments_path) setImgSrc(res.attachments_path); // show the new photo right away
      lmsToast({ toasterType: "success", msg: res.msg || "Profile photo updated successfully." });
      dispatch(fetchProfileData()); // reload getUserProfile → new attachments_path (page + header)
    } else {
      setErrorMsg(res?.msg || "Failed to update the profile picture. Please try again.");
    }
  };

  return (
    <div className="image_container--maindiv">
      <div className="profile_image">
        <img
          src={imgSrc}
          alt=""
          className="profile_image--image"
          width="100%"
          height="100%"
          onError={(e) => {
            e.target.src = avatar; // Fallback to default avatar on error
          }}
        />
        {errorMsg && <div className="error__message w-100" style={{ textAlign: "center" }}>{errorMsg}</div>}
        <div className="edit-profile_icon">
          <DmsButton
            style={{ color: "white" }}
            label={uploading ? "Uploading…" : t("editphoto")}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          />
          <input
            ref={fileInputRef}
            id="file_upload"
            type="file"
            accept="image/png, image/jpeg"
            name="file_upload"
            style={{ display: "none" }}
            onChange={handleChange}
          />
        </div>
        <div className="user-profile_detail">
          <div>
            {`${profileData?.first_name || ""} ${
              profileData?.middle_name ? profileData?.middle_name : ""
            } ${profileData?.last_name || ""}`}
          </div>
          <div>{profileData?.user_name}</div>
          <div>{profileData?.phone_number}</div>
        </div>
      </div>
    </div>
  );
}
